// src/pages/about.js
import React from 'react';
import styles from '../styles/pages/About.module.css';
import { 
  ProfilePicture, 
  Bio, 
  ResumeSection, 
  ResumeEducation, 
  ResumeExperience,
  TwoColumnList
} from '../components';
import { AboutSidebar } from '../components/layout/AboutSidebar';

/**
 * About page component that displays profile information and resume
 * Uses reusable components for profile picture, bio, and resume sections
 * Layout flipped so bio is on left and image is on right
 */
export function About() {
  // Navigation menu links - includes bio at the top
  const navLinks = [
    { id: "bio", label: "About Me" },
    { id: "experience", label: "Experience" },
    { id: "education", label: "Education" },
    { id: "certifications", label: "Certifications" },
    { id: "languages", label: "Languages" },
    { id: "skills", label: "Skills" },
    { id: "tools", label: "Frameworks & Tools" },
    { id: "hobbies", label: "Hobbies" }
  ];
  
  // Experience items data
  // Split Experience into categories: Description, Responsibilities, and Impact
  const experiences = [
    {
      title: "AI Architect",
      company: "Apple",
      location: "San Diego, California",
      period: "January 2026 - Present",
      responsibilities: [
        // roles
        "AI Expert for Apple's \
          Data Solutions & Initiatives \
          team, providing guidance on the \
          latest AI technologies and their \
          applications",
        // Responsibilities
        "Architect and implement an Agentic AI layer for an internal, high-impact data tool",
        // Impact
      ],
      size: "fortune 500"
    },
    {
      title: "Yoga Teacher",
      company: "Yoga Branch",
      location: "Vista, California",
      period: "June 2025 - Present",
      responsibilities: [
        "Teach a weekly all-levels yoga flow class",
        "RYS 200 Yoga Alliance certified instructor",
      ],
      size: "local business"
    },
    {
      title: "Senior AI/ML Product Manager",
      company: "Qualcomm",
      location: "San Diego, California",
      period: "March 2025 - January 2026",
      responsibilities: [
        // roles
        "AI Expert for Qualcomm's \
          corporate DevRel team, \
          providing guidance on the \
          latest technologies and their \
          applications to Qualcomm's \
          Cloud, Compute, Edge, IoT, XR, \
          and University Relations business \
          units (BUs)",
        "Developer Advocate for Qualcomm's \
          AI hardware and software products, including \
          AI Hub, AI Engine Direct, Qualcomm \
          Linux, and Snapdragon X Copilot+ PCs and Mobile Phones",
        // Responsibilities
        "Collaborate with Qualcomm BU \
          leadership teams to enhance \
          AI developer experiences through \
          action, advocacy, and education",
        "Orchestrate and host multiple large-scale \
          Edge AI hackathons across North America, \
          South America, Europe, and Asia",
        "Regularly engage with enterprise \
          and startup partners like Meta, \
          Microsoft, Neo4j, and Weaviate to \
          drive developer mindshare",
        "Host live streams and create videos for the Qualcomm Developer Discord and YouTube channels",
        "Author technical content for the Qualcomm Developer Blog and GitHub repositories",
        "Organize monthly discussions for the local San Diego community to discuss the latest trends in AI",
        // Impact
        "Directly engaged with 8000+ AI software engineers and counting across 50+ first party events",
        "Delivered over 125 prototype AI applications for Snapdragon X to GitHub with open source licenses",
        "Most attended Developer Workshop at Microsoft Build 2025",
      ],
      size: "fortune 500"
    },
    {
      title: "Senior Machine Learning Engineer",
      company: "Qualcomm",
      location: "San Diego, California",
      period: "March 2024 - March 2025",
      responsibilities: [
        // role
        "Led the Qualcomm Developer AI/ML \
          initiatives, with a focus on enhancing \
          developer experiences for Qualcomm's AI \
          products",
        // responsibilities
        "Built 3 open-source Edge AI/ML Python apps \
          leveraging the AI capabilities of Qualcomm's \
          latest Snapdragon X hardware",
        "Organized and led 2+ developer engagements \
          per month with 30-150 developers to grow \
          the Qualcomm Developer community",
        "Organized, led, and participated in 5+ \
          developer workflow dogfooding sessions",
        "Generated comprehensive readiness reports \
          to translate developer feedback into \
          actionable milestones for the Compute and \
          IoT BUs",
        // impact
        "Grew the Qualcomm Developer community \
          by ~300% per quarter over one year",
      ],
      size: "fortune 500"
    },
    {
      title: "Senior AI & Fullstack Software Engineer",
      company: "Aicadium",
      location: "San Diego, California",
      period: "March 2023 - March 2024",
      responsibilities: [
        "Led a team of 4 engineers to design \
          and build Aicadium Inspection, \
          an AI-powered aircraft maintenance \
          and repair monitoring and inspection tool \
          now deployed at LAX by Singapore Airlines",
        "Designed, built, and maintained several \
          distributed microservices from prototype \
          to production deployment: RBAC API, multiple \
          containerized AI inference endpoints, multiple \
          UI components, and a video clipping tool",
        "Integrated a multimodal LLM and supporting \
          data pipeline into Aicadium Inspection to \
          enable text-based image review and comparison"
      ],
      size: "startup"
    },
    {
      title: "Senior Fullstack Software & Data Engineer",
      company: "Qualcomm",
      location: "San Diego, California",
      period: "November 2021 - March 2023",
      responsibilities: [
        "Lead software engineer on the \
          Data Automation and Engineering \
          team, reporting to the SVP of \
          Qualcomm's Product and Test \
          Engineering (PTE) BU",
        "Designed and implemented a fault-tolerant, \
          scalable data processing, storage, and \
          reporting solution capable of ingesting \
          any data type and shape at production scale",
        "Led the team of global engineers \
          responsible for growing and \
          maintainig PTE's data tools \
          and infrastructure",
      ],
      size: "fortune 500"
    },
    {
      title: "Fullstack Software & Data Engineer",
      company: "Qualcomm",
      location: "San Diego, California",
      period: "August 2019 - November 2021",
      responsibilities: [
        "Designed, built, and maintained all \
          data processing automations for Qualcomm's \
          RF chip design and production teams",
        "Reduced total calculation and analysis \
          runtimes by over 400%",
        "My work was internally highlighted as a \
          top innovation across Qualcomm in a 2021 \
          global business review",
      ],
      size: "fortune 500"
    },
    {
      title: "R&D Software Engineer",
      company: "NXP Semiconductors",
      location: "Chandler, Arizona",
      period: "May 2018 - August 2019",
      responsibilities: [
        "Designed and implemented a \
          distributed microcontroller-based \
          MVP for a patented accelerometer \
          calibration algorithm (NPS) to \
          replace physical device shaking",
        "Redesigned the NPS data pipeline from \
          ingestion to inference, reducing test \
          cycle runtime over 1000%",
        "Developed a neural network based \
          NPS v2 prototype capable of greater \
          than $2 million in potential annual savings",
      ],
      size: "fortune 500"
    }
  ];
  
  // Tools and frameworks data
  const toolsItems = [
    "AnythingLLM", "AWS", "Boto", "ChatGPT", "Claude", "Django", "Docker", "FastAPI", "Flask", "Gemini", "Gemma", "HuggingFace", "LangChain", "Llama", "LM Studio", "Logic Pro",
    "NextJS", "Numpy", "Ollama", "OpenAI", "Pandas", "PyTorch", "React", "Terraform"
  ];
  
  // Hobbies data
  const hobbies = ["Making Music", "Yoga", "Chess", "Coding", "Gardening", "Baking"];

  return (
    <div className={styles.container}>
      <div className={styles.resumeLayout}>
        <div className={styles.resumeContent}>
          <div className={styles.content}>
            <div id="bio" className={styles.bioSection}>
              <Bio />
            </div>
            <div className={styles.profileSection}>
              <ProfilePicture 
                imgSrc="/headshot.jpeg"
                altText="Nick Debeurre"
                showSocialLinks={true}
              />
            </div>
          </div>

          {/* Experience Section */}
          <ResumeSection id="experience" title="Experience">
            {experiences.map((exp, index) => (
              <ResumeExperience key={index} {...exp} />
            ))}
          </ResumeSection>

          {/* Education Section */}
          <ResumeSection id="education" title="Education">
            <div className={styles.cardGrid}>
              <div className={styles.card}>
                <ResumeEducation
                  degree="Computer Science, MS"
                  institution="Arizona State University"
                  period="2018 - 2019"
                />
              </div>
              <div className={styles.card}>
                <ResumeEducation
                  degree="Computer Science, BS"
                  institution="Arizona State University"
                  period="2014 - 2018"
                />
              </div>
            </div>
          </ResumeSection>

          {/* Certification Section */}
          <ResumeSection id="certifications" title="Certifications">
            <div className={styles.cardGrid}>
              <div className={styles.card}>
                <ResumeEducation
                  degree="RYS 200"
                  institution="Yoga Alliance"
                  period="2025"
                />
              </div>
            </div>
          </ResumeSection>

          {/* Languages Section */}
          <ResumeSection id="languages" title="Languages">
            <div className={styles.cardGrid}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Computer</h3>
                <p>
                  Python - Advanced
                  <br />
                  JavaScript - Advanced
                  <br />
                  TypeScript - Advanced
                  <br />
                  SQL - Advanced
                  <br />
                  C/C++ - Generalist
                  <br />
                  C# - Generalist
                </p>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Human</h3>
                <p>
                  English - Native
                  <br />
                  French - Native
                </p>
              </div>
            </div>
          </ResumeSection>

          {/* Skills Section */}
          <ResumeSection id="skills" title="Skills">
            <div className={styles.cardGrid}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Artificial Intelligence</h3>
                <p>
                  Broad expertise in audio, computer vision, deep learning, and generative AI complemented by studies in cognitive psychology, consciousness, and philosophy. I particularly enjoy working with edge deployments, reinforcement learning, and agentic language models. I give educational talks about AI and engage in regular community discussions.
                </p>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Digital Literacy</h3>
                <p>
                  I'm OS agnostic and can read and write code in a variety of programming languages. I take pride in my ability to write simple, clean, readable code. My "native" programming language is Python, and I typically work with a React JS and FastAPI Python stack powered by shell scripts and Docker. I often interact with the physical world by incorporating hardware into my projects.
                </p>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Startup Mind, Enterprise Experience</h3>
                <p>
                  Adept at driving innovation from concept to production, typically as a solo software engineer on a lean team. Capable of leading production projects or handing off to a team for further development. I enjoy the challenge of projects with limited resources and something to prove.
                </p>
              </div>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Communication and Connection</h3>
                <p>
                  I excel at building trusting relationships, and I have a proven ability to teach to and connect with both technical and non-technical audiences alike. I regularly host technical events for worldwide audiences ranging from 5 to 400 people.
                </p>
              </div>
            </div>
          </ResumeSection>

          {/* Frameworks and Tools Section */}
          <ResumeSection id="tools" title="Frameworks and Tools">
            <TwoColumnList items={toolsItems} columnCount={3} />
          </ResumeSection>

          {/* Hobbies Section */}
          <ResumeSection id="hobbies" title="Hobbies">
            <TwoColumnList items={hobbies} columnCount={3} />
          </ResumeSection>
        </div>
        
        {/* Sidebar Navigation - positioned in margin */}
        <AboutSidebar links={navLinks} />
      </div>
    </div>
  );
}

export default About;